// Location: src/features/simulators/shared/components/ReservationNumberControl.tsx

import React from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Info as InfoIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { generateReservationNumber } from '../utils/dataGenerators';

/**
 * Translation keys
 */
const TRANSLATIONS = {
  LABEL: 'resources.simulators.shared.reservation_number.label',
  AUTO_GENERATE: 'resources.simulators.shared.reservation_number.auto_generate',
  MANUAL: 'resources.simulators.shared.reservation_number.manual',
  PLACEHOLDER: 'resources.simulators.shared.reservation_number.placeholder',
  TOOLTIP: 'resources.simulators.shared.reservation_number.tooltip',
  GENERATE_NEW: 'resources.simulators.shared.reservation_number.generate_new',
} as const;

interface ReservationNumberControlProps {
  /**
   * Current reservation number value
   */
  value: string;
  
  /**
   * Whether to auto-generate reservation numbers
   */
  autoGenerate: boolean;
  
  /**
   * Callback when reservation number changes
   */
  onValueChange: (value: string) => void;
  
  /**
   * Callback when auto-generate toggle changes
   */
  onAutoGenerateChange: (autoGenerate: boolean) => void;
  
  /**
   * Whether the simulator is currently running
   */
  disabled?: boolean;
  
  /**
   * Prefix for generated reservation numbers (default: 'RES')
   */
  prefix?: string;
}

/**
 * Reservation Number Control Component
 * 
 * Allows users to:
 * 1. Auto-generate reservation numbers (default behavior)
 * 2. Manually specify a reservation number for testing combined journeys
 * 
 * Example use case:
 * - Do pre-check-in with RES001 in Online simulator
 * - Then dispense key with same RES001 in Dispenser simulator
 * - This creates a combined journey that can be correlated
 */
export const ReservationNumberControl: React.FC<ReservationNumberControlProps> = ({
  value,
  autoGenerate,
  onValueChange,
  onAutoGenerateChange,
  disabled = false,
  prefix = 'RES',
}) => {
  const translate = useTranslate();

  /**
   * Generate a new reservation number
   */
  const handleGenerateNew = () => {
    const newReservation = generateReservationNumber(prefix);
    onValueChange(newReservation);
  };

  /**
   * Handle auto-generate toggle
   */
  const handleToggleAutoGenerate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAutoGenerate = event.target.checked;
    onAutoGenerateChange(newAutoGenerate);
    
    // If switching to manual mode and no value, generate one
    if (!newAutoGenerate && !value) {
      handleGenerateNew();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Title with tooltip */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {translate(TRANSLATIONS.LABEL)}
        </Typography>
        <Tooltip title={translate(TRANSLATIONS.TOOLTIP)}>
          <IconButton size="small" sx={{ ml: 0.5 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Auto-generate toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={autoGenerate}
            onChange={handleToggleAutoGenerate}
            disabled={disabled}
            color="primary"
          />
        }
        label={translate(
          autoGenerate 
            ? TRANSLATIONS.AUTO_GENERATE 
            : TRANSLATIONS.MANUAL
        )}
        sx={{ mb: autoGenerate ? 0 : 1 }}
      />

      {/* Manual input field */}
      {!autoGenerate && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={disabled}
            placeholder={translate(TRANSLATIONS.PLACEHOLDER)}
            fullWidth
            size="small"
            variant="outlined"
            helperText={
              <Typography variant="caption" color="text.secondary">
                {translate(TRANSLATIONS.TOOLTIP)}
              </Typography>
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }
            }}
          />
          <Tooltip title={translate(TRANSLATIONS.GENERATE_NEW)}>
            <IconButton
              onClick={handleGenerateNew}
              disabled={disabled}
              color="primary"
              size="small"
              sx={{ mt: 0.5 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};